/**
 * Search synonyms for the palette's pages and actions, in French AND English on purpose: people type in either
 * language whatever the UI language is ("create member" in a French UI). These words are only matched against,
 * never displayed, so they are not UI copy; the labels shown stay in the i18n messages.
 */

/** Verbs that announce an action ("créer un membre"): matching actions then come first. */
export const ACTION_VERBS = [
  "creer",
  "ajouter",
  "nouveau",
  "nouvelle",
  "enregistrer",
  "saisir",
  "collecter",
  "create",
  "add",
  "new",
  "record",
] as const

/** Words ignored by page and action matching (articles and the like). */
export const NAV_STOPWORDS = new Set(["un", "une", "le", "la", "les", "des", "de", "du", "d", "l", "a", "au", "aux", "the", "an", "of"])

/** Keywords per quick-create action key (`useGlobalCreateGroups` item keys); every action also gets the verbs. */
export const ACTION_KEYWORDS: Record<string, readonly string[]> = {
  offering: ["offrande", "don", "dime", "quete", "entree", "recette", "offering", "gift", "tithe", "donation", "income"],
  expense: ["depense", "sortie", "paiement", "achat", "expense", "payment", "spend"],
  transfer: ["transfert", "virement", "remise", "transfer", "remittance"],
  pledge: ["promesse", "engagement", "pledge", "promise"],
  budget: ["budget", "prevision", "forecast"],
  member: ["membre", "fidele", "personne", "member", "person", "people"],
  visitor: ["visiteur", "visiteuse", "invite", "visitor", "guest"],
  minister: ["ministre", "pasteur", "serviteur", "minister", "pastor"],
  activity: ["activite", "evenement", "culte", "programme", "reunion", "activity", "event", "service", "meeting"],
  asset: ["actif", "bien", "materiel", "equipement", "asset", "equipment", "item"],
}

/** Keywords per page path, most specific first; a page without its own entry inherits its top-level module's. */
export const PAGE_KEYWORDS: Record<string, readonly string[]> = {
  "/": ["accueil", "tableau de bord", "home", "dashboard"],
  "/members": ["membres", "fidele", "personnes", "annuaire", "members", "people", "directory"],
  "/members/groups": ["groupes", "cellules", "groups", "cells"],
  "/members/ministries": ["ministeres", "ministries"],
  "/members/baptism": ["bapteme", "baptism"],
  "/activities": ["activites", "evenements", "cultes", "programmes", "calendrier", "activities", "events", "calendar"],
  "/departments": ["departements", "departments"],
  "/finance": ["finance", "finances", "argent", "caisse", "money"],
  "/finance/transactions": ["transactions", "operations", "dons", "offrandes", "depenses", "gifts", "expenses"],
  "/finance/pledges": ["promesses", "engagements", "pledges"],
  "/finance/transfers": ["transferts", "virements", "remises", "transfers", "remittances"],
  "/finance/accounts": ["comptes", "fonds", "accounts", "funds"],
  "/finance/reconciliation": ["rapprochement", "reconciliation"],
  "/finance/investigations": ["audit", "enquete", "investigation"],
  "/finance/settings": ["parametres", "configuration", "settings"],
  "/reports": ["rapports", "etats", "reports", "statements"],
  "/budgets": ["budgets", "previsions", "forecast"],
  "/assets": ["actifs", "biens", "materiel", "inventaire", "assets", "inventory", "equipment"],
  "/forms": ["formulaires", "inscriptions", "forms", "registrations"],
  "/audit-log": ["journal", "historique", "audit", "log", "history"],
  "/my-branch": ["ma branche", "equipe", "branch", "team"],
  "/settings": ["parametres", "reglages", "settings"],
  "/ministries": ["ministeres", "ministries"],
  "/work-queue": ["taches", "validations", "approbations", "tasks", "approvals"],
}

export function pageKeywords(path: string): readonly string[] {
  const clean = path.split(/[?#]/)[0] ?? path
  const own = PAGE_KEYWORDS[clean] ?? []
  const top = `/${clean.split("/").filter(Boolean)[0] ?? ""}`
  const module = top !== clean ? (PAGE_KEYWORDS[top] ?? []) : []
  return [...own, ...module]
}

export function actionKeywords(key: string): readonly string[] {
  return [...ACTION_VERBS, ...(ACTION_KEYWORDS[key] ?? [])]
}
